import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { SuvoHelpTextDialogComponent } from './suvo-help-text-dialog.component';

export type SuvoHelpTextTrigger = 'text' | 'info' | 'question';

@Component({
  selector: 'suvo-help-text',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule],
  template: `
    <span class="trigger" [class.trigger--pipe]="showPipe">
      @if (trigger === 'text') {
        <a
          class="link"
          href="#"
          [matTooltip]="tooltip ? text : null"
          [matTooltipDisabled]="!tooltip"
          matTooltipPosition="above"
          (click)="onLinkClick($event)"
        >
          {{ label }}
        </a>
      } @else {
        <button
          type="button"
          class="btn btn--icon"
          [attr.aria-label]="label"
          [matTooltip]="tooltip ? text : null"
          [matTooltipDisabled]="!tooltip"
          matTooltipPosition="above"
          (click)="onClick()"
        >
          @switch (trigger) {
            @case ('info') {
              <span class="icon-circle" aria-hidden="true">
                <mat-icon inline>info</mat-icon>
              </span>
            }
            @case ('question') {
              <span class="icon-circle" aria-hidden="true">
                <mat-icon inline>help</mat-icon>
              </span>
            }
          }
        </button>
      }
    </span>
  `,
  styles: [
    `
      .trigger {
        display: inline-flex;
        align-items: baseline;
        gap: 8px;
        line-height: inherit;
      }

      .trigger--pipe {
        position: relative;
        margin-left: 10px;
        padding-left: 10px;
      }

      .trigger--pipe::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0.15em;
        bottom: 0.15em;
        border-left: 0.5px solid rgba(0, 0, 0, 0.35);
      }

      .link {
        font-size: 12px;
        color: #0062a1;
        text-decoration: none;
        font-weight: 600;
        cursor: pointer;
        outline: none;
        line-height: inherit;
      }

      .link:visited {
        color: #0062a1;
      }

      .link:focus-visible {
        border-radius: 4px;
        outline: 2px solid rgba(25, 118, 210, 0.55);
        outline-offset: 2px;
      }

      .btn {
        padding: 0;
        min-width: unset;
        line-height: inherit;
        height: auto;
        text-transform: none;
        border-radius: 4px;
        font-weight: 600;
      }

      .icon-circle {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 1px solid rgba(0, 0, 0, 0.4);
        color: rgba(0, 0, 0, 0.7);
        background: rgba(0, 0, 0, 0.02);
      }

      .icon-circle mat-icon {
        font-size: 14px;
        width: 14px;
        height: 14px;
      }
    `,
  ],
})
export class SuvoHelpTextComponent {
  /** The help text shown in the dialog/tooltip. */
  @Input({ required: true }) text!: string;

  /** Trigger style: 'text' shows a link label, 'info'/'question' show an icon in a circle. */
  @Input() trigger: SuvoHelpTextTrigger = 'text';

  /** Link label when trigger='text'. */
  @Input() label = 'Info';

  /** Optional title for the dialog. */
  @Input() title = 'Info';

  /** When true, shows content as a tooltip instead of opening a dialog. */
  @Input() tooltip = false;

  /** When true, renders a leading pipe separator. */
  @Input() showPipe = false;

  /** When true, renders dialog content as HTML via [innerHTML]. */
  @Input() html = false;

  constructor(private readonly dialog: MatDialog) {}

  onLinkClick(event: MouseEvent): void {
    event.preventDefault();
    this.onClick();
  }

  onClick(): void {
    if (this.tooltip) return;

    this.dialog.open(SuvoHelpTextDialogComponent, {
      data: { title: this.title, text: this.text, html: this.html },
      width: '520px',
    });
  }
}
